<template>
  <div v-if="!hideOnCurrentRoute">
    <!-- Main Mobile Bottom Navigation Bar -->
    <nav
      class="fixed bottom-0 left-0 right-0 z-40 bg-background-light/95 dark:bg-slate-900/95 backdrop-blur-xl border-t border-background-lighter dark:border-slate-800 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] flex md:hidden items-center justify-around px-2 py-1 pb-[max(env(safe-area-inset-bottom),8px)]"
      aria-label="Mobile Navigation"
    >
      <!-- 1. Home -->
      <router-link
        to="/"
        class="flex flex-col items-center justify-center flex-1 py-1 px-1 transition-all duration-200 group active:scale-90"
        :class="isHomeActive ? 'text-primary font-bold' : 'text-text-secondary hover:text-text-primary'"
      >
        <div class="relative">
          <svg class="w-5 h-5 transition-transform duration-200 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
          </svg>
          <span v-if="isHomeActive" class="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary rounded-full"></span>
        </div>
        <span class="text-[10px] mt-1 tracking-tight">Home</span>
      </router-link>

      <!-- 2. Categories -->
      <button
        type="button"
        @click="handleCategoriesClick"
        class="flex flex-col items-center justify-center flex-1 py-1 px-1 transition-all duration-200 group active:scale-90"
        :class="isCategoriesActive ? 'text-primary font-bold' : 'text-text-secondary hover:text-text-primary'"
      >
        <div class="relative">
          <svg class="w-5 h-5 transition-transform duration-200 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/>
          </svg>
          <span v-if="isCategoriesActive" class="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary rounded-full"></span>
        </div>
        <span class="text-[10px] mt-1 tracking-tight">Categories</span>
      </button>

      <!-- 3. Basket Button (Always visible for unlogged guests and inactive users) -->
      <button
        v-if="canShowBasket"
        type="button"
        @click="cartStore.toggleCart"
        class="flex flex-col items-center justify-center flex-1 py-1 px-1 transition-all duration-200 group active:scale-90 relative"
        :class="isCartActive ? 'text-primary font-bold' : 'text-text-secondary hover:text-text-primary'"
        aria-label="Shopping Basket"
      >
        <div class="relative">
          <svg class="w-5 h-5 transition-transform duration-200 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <span
            v-if="cartStore.count > 0"
            class="absolute -top-1.5 -right-2.5 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-full text-[9px] font-black h-4 min-w-[16px] px-1 flex items-center justify-center shadow-md animate-pulse"
          >
            {{ cartStore.count > 99 ? '99+' : cartStore.count }}
          </span>
          <span v-if="isCartActive" class="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary rounded-full"></span>
        </div>
        <span class="text-[10px] mt-1 tracking-tight">Basket</span>
      </button>

      <!-- 4. Orders (if logged in) or Hardware Store (if guest) -->
      <router-link
        v-if="authStore.isLoggedIn"
        to="/panel/orders"
        class="flex flex-col items-center justify-center flex-1 py-1 px-1 transition-all duration-200 group active:scale-90"
        :class="isOrdersActive ? 'text-primary font-bold' : 'text-text-secondary hover:text-text-primary'"
      >
        <div class="relative">
          <svg class="w-5 h-5 transition-transform duration-200 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
          </svg>
          <span v-if="isOrdersActive" class="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary rounded-full"></span>
        </div>
        <span class="text-[10px] mt-1 tracking-tight">Orders</span>
      </router-link>

      <router-link
        v-else
        to="/physical-catalog"
        class="flex flex-col items-center justify-center flex-1 py-1 px-1 transition-all duration-200 group active:scale-90"
        :class="isHardwareActive ? 'text-primary font-bold' : 'text-text-secondary hover:text-text-primary'"
      >
        <div class="relative">
          <svg class="w-5 h-5 transition-transform duration-200 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          <span v-if="isHardwareActive" class="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary rounded-full"></span>
        </div>
        <span class="text-[10px] mt-1 tracking-tight">Hardware</span>
      </router-link>

      <!-- 5. Profile / Account (Logged in) or Login (Guest) -->
      <button
        v-if="authStore.isLoggedIn"
        type="button"
        @click="showAccountSheet = true"
        class="flex flex-col items-center justify-center flex-1 py-1 px-1 transition-all duration-200 group active:scale-90"
        :class="isProfileActive ? 'text-primary font-bold' : 'text-text-secondary hover:text-text-primary'"
      >
        <div class="relative">
          <div class="w-5 h-5 rounded-full border border-current flex items-center justify-center text-[10px] font-bold overflow-hidden bg-primary/10">
            <span>{{ userInitial }}</span>
          </div>
          <span v-if="isProfileActive" class="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary rounded-full"></span>
        </div>
        <span class="text-[10px] mt-1 tracking-tight truncate max-w-[50px]">{{ authStore.currentUser || 'Account' }}</span>
      </button>

      <!-- Creative Login Button (When Not Connected) -->
      <router-link
        v-else
        :to="{ path: '/login', query: { redirect: $route.fullPath } }"
        class="flex flex-col items-center justify-center flex-1 py-1 px-1 transition-all duration-200 group active:scale-90"
        :class="isLoginActive ? 'text-primary font-bold' : 'text-primary hover:text-primary-dark'"
      >
        <div class="relative px-2 py-0.5 rounded-xl bg-primary/10 border border-primary/25 group-hover:bg-primary/20 transition-all flex items-center justify-center shadow-xs">
          <svg class="w-4.5 h-4.5 text-primary transition-transform duration-200 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
          </svg>
          <span v-if="isLoginActive" class="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary rounded-full"></span>
        </div>
        <span class="text-[10px] mt-0.5 tracking-tight font-bold text-primary">Login</span>
      </router-link>
    </nav>

    <!-- Slide-Up Account Bottom Sheet Modal -->
    <Transition name="fade">
      <div
        v-if="showAccountSheet"
        class="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm md:hidden"
        @click="showAccountSheet = false"
      ></div>
    </Transition>

    <Transition name="slide-up">
      <div
        v-if="showAccountSheet"
        class="fixed bottom-0 left-0 right-0 z-50 bg-background-light dark:bg-slate-900 border-t border-background-lighter dark:border-slate-800 rounded-t-3xl shadow-2xl p-5 pb-[max(env(safe-area-inset-bottom),20px)] md:hidden max-h-[85vh] overflow-y-auto"
        @click.stop
      >
        <!-- Pull handle -->
        <div class="w-12 h-1.5 bg-background-lighter dark:bg-slate-700 rounded-full mx-auto mb-4 cursor-pointer" @click="showAccountSheet = false"></div>

        <!-- User Info Card -->
        <div class="flex items-center justify-between pb-4 mb-4 border-b border-background-lighter dark:border-slate-800">
          <div class="flex items-center gap-3">
            <div class="w-12 h-12 rounded-2xl bg-gradient-to-tr from-primary to-indigo-600 text-white font-black text-lg flex items-center justify-center shadow-md">
              {{ userInitial }}
            </div>
            <div>
              <div class="font-bold text-text-primary dark:text-white flex items-center gap-1.5">
                <span>{{ authStore.currentUser }}</span>
                <span v-if="authStore.user?.is_active" class="w-2 h-2 rounded-full bg-emerald-500" title="Active"></span>
              </div>
              <div class="text-xs text-text-secondary truncate max-w-[190px]">
                {{ authStore.user?.email || 'Logged in' }}
              </div>
            </div>
          </div>
          <button
            @click="showAccountSheet = false"
            class="p-2 rounded-xl text-text-secondary hover:text-text-primary hover:bg-background-lighter dark:hover:bg-slate-800 transition-colors"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Wallet Card (if enabled) -->
        <div
          v-if="!dataStore.shouldUseDAPricing && !dataStore.shouldHideWallet"
          class="mb-4 p-3.5 rounded-2xl bg-gradient-to-r from-primary/10 to-indigo-500/10 border border-primary/20 flex items-center justify-between"
        >
          <div>
            <div class="text-xs text-text-secondary font-medium">Available Balance</div>
            <div class="text-lg font-black text-primary">{{ authStore.formattedWallet }}</div>
          </div>
          <router-link
            to="/panel/charge-wallet"
            class="px-3.5 py-1.5 rounded-xl bg-primary text-white text-xs font-bold hover:bg-primary-dark transition-colors shadow-sm"
            @click="showAccountSheet = false"
          >
            + Top Up
          </router-link>
        </div>

        <!-- Navigation Links -->
        <div class="space-y-1">
          <router-link
            to="/panel/profile"
            class="flex items-center gap-3.5 px-3.5 py-2.5 rounded-xl text-sm font-medium hover:bg-background-lighter dark:hover:bg-slate-800 text-text-primary dark:text-slate-200 transition-colors"
            @click="showAccountSheet = false"
          >
            <div class="w-8 h-8 rounded-lg bg-blue-500/10 text-blue-500 flex items-center justify-center">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <span>Profile & Account</span>
          </router-link>

          <router-link
            to="/panel/orders"
            class="flex items-center gap-3.5 px-3.5 py-2.5 rounded-xl text-sm font-medium hover:bg-background-lighter dark:hover:bg-slate-800 text-text-primary dark:text-slate-200 transition-colors"
            @click="showAccountSheet = false"
          >
            <div class="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </div>
            <span>My Orders</span>
          </router-link>

          <router-link
            v-if="!dataStore.shouldUseDAPricing && !dataStore.shouldHideWallet"
            to="/panel/charge-wallet"
            class="flex items-center gap-3.5 px-3.5 py-2.5 rounded-xl text-sm font-medium hover:bg-background-lighter dark:hover:bg-slate-800 text-text-primary dark:text-slate-200 transition-colors"
            @click="showAccountSheet = false"
          >
            <div class="w-8 h-8 rounded-lg bg-amber-500/10 text-amber-500 flex items-center justify-center">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <span>Charge Wallet</span>
          </router-link>

          <button
            type="button"
            @click="togglePriceVisibility"
            :disabled="isUpdatingPreference"
            class="w-full flex items-center gap-3.5 px-3.5 py-2.5 rounded-xl text-sm font-medium hover:bg-background-lighter dark:hover:bg-slate-800 text-text-primary dark:text-slate-200 transition-colors disabled:opacity-60 text-left"
          >
            <div class="w-8 h-8 rounded-lg bg-purple-500/10 text-purple-500 flex items-center justify-center">
              <svg v-if="isUpdatingPreference" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <svg v-else-if="isPricesHidden" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
              </svg>
              <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
            <span>{{ isPricesHidden ? 'Show Prices' : 'Hide Prices' }}</span>
          </button>

          <div class="px-3.5 py-1">
            <theme-toggle menu-item />
          </div>

          <div class="border-t border-background-lighter dark:border-slate-800 my-2"></div>

          <button
            type="button"
            @click="handleLogout"
            class="w-full flex items-center gap-3.5 px-3.5 py-2.5 rounded-xl text-sm font-medium hover:bg-red-500/10 text-red-500 transition-colors text-left"
          >
            <div class="w-8 h-8 rounded-lg bg-red-500/10 text-red-500 flex items-center justify-center">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </div>
            <span>Logout</span>
          </button>
        </div>
      </div>
    </Transition>

    <!-- Slide-Up Categories Bottom Sheet Modal -->
    <Transition name="fade">
      <div
        v-if="showCategoriesSheet"
        class="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm md:hidden"
        @click="showCategoriesSheet = false"
      ></div>
    </Transition>

    <Transition name="slide-up">
      <div
        v-if="showCategoriesSheet"
        class="fixed bottom-0 left-0 right-0 z-50 bg-background-light dark:bg-slate-900 border-t border-background-lighter dark:border-slate-800 rounded-t-3xl shadow-2xl p-4 pb-[max(env(safe-area-inset-bottom),20px)] md:hidden max-h-[85vh] flex flex-col"
        @click.stop
      >
        <!-- Pull handle -->
        <div class="w-12 h-1.5 bg-background-lighter dark:bg-slate-700 rounded-full mx-auto mb-3 cursor-pointer" @click="showCategoriesSheet = false"></div>

        <!-- Categories Header -->
        <div class="flex items-center justify-between pb-3 mb-3 border-b border-background-lighter dark:border-slate-800 flex-shrink-0">
          <div class="flex items-center gap-2.5">
            <div class="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/>
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-base text-text-primary dark:text-white">Browse Categories</h3>
              <p class="text-xs text-text-secondary">{{ allCategories.length }} categories available</p>
            </div>
          </div>
          <button
            type="button"
            @click="showCategoriesSheet = false"
            class="p-2 rounded-xl text-text-secondary hover:text-text-primary hover:bg-background-lighter dark:hover:bg-slate-800 transition-colors"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Search Categories & Subcategories Input -->
        <div class="mb-3 flex-shrink-0">
          <div class="relative">
            <input
              type="text"
              v-model="categorySearchQuery"
              placeholder="Search games, gift cards, software..."
              class="w-full bg-background dark:bg-slate-800 border border-background-lighter dark:border-slate-700 rounded-xl pl-9 pr-8 py-2 text-xs text-text-primary dark:text-slate-100 placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <svg class="w-4 h-4 absolute left-3 top-2.5 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <button
              v-if="categorySearchQuery"
              type="button"
              @click="categorySearchQuery = ''"
              class="absolute right-2.5 top-2 text-text-secondary hover:text-text-primary p-0.5"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Quick Store Links -->
        <div class="flex gap-2 mb-3 pb-2 border-b border-background-lighter dark:border-slate-800 overflow-x-auto hide-scrollbar flex-shrink-0">
          <router-link
            to="/all-products"
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-white text-xs font-bold whitespace-nowrap transition-colors"
            @click="showCategoriesSheet = false"
          >
            <span>🛍️</span>
            <span>All Products</span>
          </router-link>
          <router-link
            to="/physical-catalog"
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 hover:bg-blue-600 hover:text-white text-xs font-bold whitespace-nowrap transition-colors"
            @click="showCategoriesSheet = false"
          >
            <span>📦</span>
            <span>Hardware Store</span>
          </router-link>
          <router-link
            v-if="isRechargeAvailable"
            to="/recharge"
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-600 hover:text-white text-xs font-bold whitespace-nowrap transition-colors"
            @click="showCategoriesSheet = false"
          >
            <span>⚡</span>
            <span>Recharge</span>
          </router-link>
        </div>

        <!-- Scrollable Category List -->
        <div class="flex-1 overflow-y-auto space-y-2 pr-1">
          <div
            v-for="category in filteredCategories"
            :key="`cat-${category.id}`"
            class="rounded-2xl border border-background-lighter dark:border-slate-800 bg-background/50 dark:bg-slate-800/40 overflow-hidden transition-all duration-200"
          >
            <!-- Category Header / Accordion trigger -->
            <div
              @click="toggleCategory(category.id)"
              class="flex items-center justify-between p-3 cursor-pointer hover:bg-background-lighter/60 dark:hover:bg-slate-800/80 transition-colors"
            >
              <div class="flex items-center gap-3 min-w-0">
                <div class="w-10 h-10 rounded-xl bg-background-lighter dark:bg-slate-700/60 flex items-center justify-center overflow-hidden flex-shrink-0 shadow-xs">
                  <img
                    :src="getImageUrl(category)"
                    :alt="category.libelle"
                    class="w-full h-full object-contain p-1"
                    @error="handleImageError"
                  />
                </div>
                <div class="min-w-0">
                  <h4 class="font-bold text-sm text-text-primary dark:text-slate-100 truncate">
                    {{ category.libelle }}
                  </h4>
                  <span class="text-[11px] text-text-secondary">
                    {{ getFilteredSubcategories(category.id).length }} items
                  </span>
                </div>
              </div>

              <div class="flex items-center gap-1.5 flex-shrink-0">
                <button
                  type="button"
                  @click.stop="navigateToCategory(category)"
                  class="px-2 py-1 text-[11px] font-semibold text-primary hover:underline"
                >
                  View
                </button>
                <div
                  class="w-6 h-6 rounded-lg flex items-center justify-center text-text-secondary transition-transform duration-200"
                  :class="{ 'rotate-180 text-primary': expandedCategoryId === category.id || categorySearchQuery }"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            <!-- Subcategories (shown when expanded or searching) -->
            <div
              v-if="expandedCategoryId === category.id || categorySearchQuery"
              class="border-t border-background-lighter dark:border-slate-800/60 bg-background-light/40 dark:bg-slate-900/40 p-2 space-y-1"
            >
              <div
                v-for="sub in getFilteredSubcategories(category.id)"
                :key="`sub-${sub.id}`"
                @click="navigateToSubcategory(sub)"
                class="flex items-center gap-2.5 p-2 rounded-xl hover:bg-background-lighter dark:hover:bg-slate-800 cursor-pointer transition-colors group"
              >
                <div class="w-8 h-8 rounded-lg bg-background dark:bg-slate-800 flex items-center justify-center overflow-hidden flex-shrink-0">
                  <img
                    :src="getImageUrl(sub)"
                    :alt="sub.libelle"
                    class="w-full h-full object-contain p-0.5 group-hover:scale-110 transition-transform"
                    @error="handleImageError"
                  />
                </div>
                <span class="text-xs font-medium text-text-primary dark:text-slate-200 group-hover:text-primary transition-colors truncate">
                  {{ sub.libelle }}
                </span>
              </div>

              <div v-if="getFilteredSubcategories(category.id).length === 0" class="p-2 text-center text-xs text-text-secondary">
                No matching items
              </div>
            </div>
          </div>

          <!-- Empty state when searching -->
          <div v-if="filteredCategories.length === 0" class="py-8 text-center text-text-secondary">
            <p class="text-sm">No categories found matching "{{ categorySearchQuery }}"</p>
            <router-link
              to="/all-products"
              class="mt-2 inline-block text-xs font-bold text-primary underline"
              @click="showCategoriesSheet = false"
            >
              Browse all products
            </router-link>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { useCartStore } from '../stores/cartStore';
import { useDataStore } from '../stores/dataStore';
import ThemeToggle from './ui/ThemeToggle.vue';
import { updateUserPreferences } from '../services/api';
import { slugify } from '@/utils/slugify';

export default {
  name: 'MobileBottomBar',
  components: {
    ThemeToggle
  },
  props: {
    sidebarOpen: {
      type: Boolean,
      default: false
    }
  },
  emits: ['toggle-sidebar'],
  setup(props, { emit }) {
    const route = useRoute();
    const router = useRouter();
    const authStore = useAuthStore();
    const cartStore = useCartStore();
    const dataStore = useDataStore();

    const showAccountSheet = ref(false);
    const showCategoriesSheet = ref(false);
    const categorySearchQuery = ref('');
    const expandedCategoryId = ref(null);
    const isUpdatingPreference = ref(false);

    const imageBaseUrl = import.meta.env.VITE_IMAGE_URL || 'http://127.0.0.1:8000/images';
    const getImageUrl = (item) => {
      if (!item) return '/placeholder-image.png';
      return item.image_url || (item.image ? `${imageBaseUrl}/${item.image}` : '/placeholder-image.png');
    };

    const handleImageError = (event) => {
      event.target.src = '/placeholder-image.png';
    };

    // Hide bottom bar on routes that have their own sticky CTA to prevent overlap
    const hideOnCurrentRoute = computed(() => {
      const name = route.name;
      const path = route.path;
      return name === 'physical-product-detail' ||
             name === 'physical-order-checkout' ||
             path.includes('/physical-order/checkout');
    });

    const canShowBasket = computed(() => {
      if (!authStore.isLoggedIn) return true;
      const isActive = authStore.user?.is_active === 1 || authStore.user?.is_active === '1';
      if (!isActive) return true;
      return !dataStore.shouldHideCart;
    });

    const isRechargeAvailable = computed(() => {
      if (!authStore.isLoggedIn || !authStore.user) return false;
      const country = dataStore.country || authStore.user?.country || '';
      const isActive = authStore.user?.is_active === '1' || authStore.user?.is_active === 1;
      const loweredCountry = country.toString().toLowerCase();
      return (loweredCountry === 'dz' || loweredCountry === '2') && isActive;
    });

    // Categories data
    const allCategories = computed(() => {
      return (dataStore.categories || []).filter(c => ![33, 34, 35].includes(parseInt(c.id)));
    });

    const getSubcategories = (categoryId) => {
      return (dataStore.subcategories || []).filter(s =>
        Number(s.categorie_id) === Number(categoryId) && ![162, 163].includes(parseInt(s.id))
      );
    };

    const filteredCategories = computed(() => {
      const query = categorySearchQuery.value.trim().toLowerCase();
      if (!query) return allCategories.value;

      return allCategories.value.filter(cat => {
        const matchCat = (cat.libelle || '').toLowerCase().includes(query);
        const subcats = getSubcategories(cat.id);
        const matchSub = subcats.some(s => (s.libelle || '').toLowerCase().includes(query));
        return matchCat || matchSub;
      });
    });

    const getFilteredSubcategories = (categoryId) => {
      const subcats = getSubcategories(categoryId);
      const query = categorySearchQuery.value.trim().toLowerCase();
      if (!query) return subcats;
      return subcats.filter(s => (s.libelle || '').toLowerCase().includes(query));
    };

    const toggleCategory = (categoryId) => {
      if (expandedCategoryId.value === categoryId) {
        expandedCategoryId.value = null;
      } else {
        expandedCategoryId.value = categoryId;
      }
    };

    const navigateToSubcategory = (subcategory) => {
      showCategoriesSheet.value = false;
      const path = dataStore.getSubcategorySlugPath(subcategory.id);
      if (path) {
        router.push(path);
      } else {
        router.push(`/subcategory/${subcategory.id}`);
      }
    };

    const navigateToCategory = (category) => {
      showCategoriesSheet.value = false;
      const path = dataStore.getCategorySlugPath(category.id);
      if (path) {
        router.push(path);
      } else {
        router.push(`/${slugify(category.libelle)}`);
      }
    };

    const isHomeActive = computed(() => route.path === '/');
    const isCategoriesActive = computed(() => showCategoriesSheet.value || route.name === 'subcategory-detail' || route.name === 'category-listing');
    const isCartActive = computed(() => cartStore.isOpen || route.path.includes('/checkout'));
    const isOrdersActive = computed(() => route.path.includes('/panel/orders'));
    const isHardwareActive = computed(() => route.path.includes('/physical'));
    const isProfileActive = computed(() => showAccountSheet.value || route.path.startsWith('/panel'));
    const isLoginActive = computed(() => route.path === '/login');

    const userInitial = computed(() => {
      if (!authStore.currentUser) return 'U';
      return authStore.currentUser.charAt(0).toUpperCase();
    });

    const isPricesHidden = computed(() => {
      if (!authStore.user) return false;
      return authStore.user.hide_price === 1;
    });

    const handleCategoriesClick = () => {
      showAccountSheet.value = false;
      showCategoriesSheet.value = !showCategoriesSheet.value;
    };

    const togglePriceVisibility = async () => {
      if (isUpdatingPreference.value || !authStore.user) return;

      const previous = authStore.user.hide_price;
      authStore.user = { ...authStore.user, hide_price: previous === 1 ? 0 : 1 };
      isUpdatingPreference.value = true;

      try {
        const response = await updateUserPreferences({ hide_price: authStore.user.hide_price });
        if (response && response.user) {
          authStore.user = {
            ...authStore.user,
            hide_price: response.user.hide_price
          };
          localStorage.setItem('user', JSON.stringify(authStore.user));
          dataStore.setUserData(authStore.user);
        }
      } catch (error) {
        authStore.user = { ...authStore.user, hide_price: previous };
        console.error('Failed to update price visibility:', error);
      } finally {
        isUpdatingPreference.value = false;
      }
    };

    const handleLogout = async () => {
      showAccountSheet.value = false;
      await authStore.logout();
      router.push('/');
    };

    // Close bottom sheets when navigating to another route
    watch(() => route.fullPath, () => {
      showAccountSheet.value = false;
      showCategoriesSheet.value = false;
    });

    return {
      authStore,
      cartStore,
      dataStore,
      showAccountSheet,
      showCategoriesSheet,
      categorySearchQuery,
      expandedCategoryId,
      allCategories,
      filteredCategories,
      getFilteredSubcategories,
      toggleCategory,
      navigateToSubcategory,
      navigateToCategory,
      getImageUrl,
      handleImageError,
      isRechargeAvailable,
      isUpdatingPreference,
      canShowBasket,
      hideOnCurrentRoute,
      isHomeActive,
      isCategoriesActive,
      isCartActive,
      isOrdersActive,
      isHardwareActive,
      isProfileActive,
      isLoginActive,
      userInitial,
      isPricesHidden,
      handleCategoriesClick,
      togglePriceVisibility,
      handleLogout
    };
  }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}
</style>
